/**
 * Experimental sleep baseline (spec 7.2). Every result is labelled
 * experimental until evaluation on real data supports it.
 *
 * Only completed sessions train the model. A running sleep is reported
 * separately: its likely wake window comes from the durations of comparable
 * completed sessions (same local time-of-day group).
 */
import type { LocalEvent } from '@/lib/schemas';
import { localParts } from '@/lib/time';
import { DAY_MS, MINUTE_MS, asOf, recencyWeight, toWindow, windowStats, type PredictionWindow, type Sample } from './common';

export const SLEEP_MODEL_VERSION = 'sleep-baseline-v1';

export interface SleepOptions {
  lookbackDays: number;
  halfLifeDays: number;
  minSamples: number;
  /** Wake gaps above this are treated as untracked time. */
  maxWakeMin: number;
  /** Sessions longer than this are treated as data errors. */
  maxSessionMin: number;
  lowerQ: number;
  upperQ: number;
}

export const DEFAULT_SLEEP_OPTIONS: SleepOptions = {
  lookbackDays: 21,
  halfLifeDays: 7,
  minSamples: 10,
  maxWakeMin: 6 * 60,
  maxSessionMin: 16 * 60,
  lowerQ: 0.2,
  upperQ: 0.8,
};

export type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night';

/** Local-hour groups: morning 05–12, afternoon 12–17, evening 17–21, night otherwise. */
export function timeOfDay(ms: number, tz: string): TimeOfDay {
  const h = localParts(ms, tz).hour;
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 21) return 'evening';
  return 'night';
}

interface Estimate {
  window: PredictionWindow;
  medianMin: number;
  sampleSize: number;
  group: TimeOfDay | 'all';
  widened: boolean;
}

interface Insufficient {
  status: 'insufficient';
  sampleSize: number;
  required: number;
  reason: string;
}

export type SleepPrediction =
  | ({
      /** Awake: estimate when the next sleep starts and how long it may last. */
      status: 'awake';
      modelVersion: string;
      experimental: true;
      basisEventId: string;
      basisMs: number;
      overdue: boolean;
      duration: { medianMin: number; lowerMin: number; upperMin: number; sampleSize: number; group: TimeOfDay | 'all' } | null;
    } & Estimate)
  | ({
      /** A sleep is running: estimate when it may end. Never trains on it. */
      status: 'sleeping';
      modelVersion: string;
      experimental: true;
      basisEventId: string;
      basisMs: number;
      overdue: boolean;
    } & Estimate)
  | (Insufficient & { modelVersion: string; experimental: true; running: boolean });

interface Session {
  id: string;
  startMs: number;
  endMs: number;
}

/** Completed, plausible sessions known at the prediction time, oldest first. */
export function completedSessions(events: LocalEvent[], opts: SleepOptions = DEFAULT_SLEEP_OPTIONS): Session[] {
  return events
    .filter((e) => e.type === 'sleep' && !e.deleted_at && e.ended_ms !== null)
    .map((e) => ({ id: e.id, startMs: e.started_ms, endMs: e.ended_ms! }))
    .filter((s) => s.endMs > s.startMs && s.endMs - s.startMs <= opts.maxSessionMin * MINUTE_MS)
    .sort((a, b) => a.startMs - b.startMs || a.id.localeCompare(b.id));
}

function estimate(
  values: Array<{ minutes: number; atMs: number; group: TimeOfDay }>,
  wanted: TimeOfDay,
  nowMs: number,
  opts: SleepOptions,
): Omit<Estimate, 'window'> & { lowerMin: number; upperMin: number } {
  const grouped = values.filter((v) => v.group === wanted);
  const useGroup = grouped.length >= opts.minSamples;
  const chosen = useGroup ? grouped : values;
  const samples: Sample[] = chosen.map((v) => ({ value: v.minutes, weight: recencyWeight(nowMs - v.atMs, opts.halfLifeDays) }));
  const stats = windowStats(samples, { lowerQ: opts.lowerQ, upperQ: opts.upperQ, minSamples: opts.minSamples });
  return { medianMin: stats.medianMin, lowerMin: stats.lowerMin, upperMin: stats.upperMin, sampleSize: chosen.length, group: useGroup ? wanted : 'all', widened: stats.widened };
}

export function computeSleepPrediction(
  events: LocalEvent[],
  tz: string,
  nowMs: number,
  options: Partial<SleepOptions> = {},
): SleepPrediction {
  const opts = { ...DEFAULT_SLEEP_OPTIONS, ...options };
  const known = asOf(events, nowMs);
  const running = known
    .filter((e) => e.type === 'sleep' && e.ended_ms === null)
    .sort((a, b) => b.started_ms - a.started_ms)[0];
  const recentCutoff = nowMs - opts.lookbackDays * DAY_MS;
  const sessions = completedSessions(known, opts);
  const recentSessions = sessions.filter((s) => s.endMs >= recentCutoff);
  const durations = recentSessions.map((s) => ({ minutes: (s.endMs - s.startMs) / MINUTE_MS, atMs: s.endMs, group: timeOfDay(s.startMs, tz) }));
  const insufficient = (sampleSize: number, reason: string): SleepPrediction => ({
    status: 'insufficient', modelVersion: SLEEP_MODEL_VERSION, experimental: true, running: !!running, sampleSize, required: opts.minSamples, reason,
  });

  if (running) {
    if (durations.length < opts.minSamples)
      return insufficient(durations.length, `Not enough data: ${durations.length} of ${opts.minSamples} recent completed sleeps.`);
    const est = estimate(durations, timeOfDay(running.started_ms, tz), nowMs, opts);
    const window = toWindow(running.started_ms, est);
    return { status: 'sleeping', modelVersion: SLEEP_MODEL_VERSION, experimental: true, basisEventId: running.id, basisMs: running.started_ms, overdue: nowMs > window.endMs, window, medianMin: est.medianMin, sampleSize: est.sampleSize, group: est.group, widened: est.widened };
  }

  if (!sessions.length) return insufficient(0, 'No completed sleeps recorded yet.');
  const wakes: Array<{ minutes: number; atMs: number; group: TimeOfDay }> = [];
  for (let i = 1; i < sessions.length; i++) {
    const minutes = (sessions[i].startMs - sessions[i - 1].endMs) / MINUTE_MS;
    if (!(minutes > 0) || minutes > opts.maxWakeMin || sessions[i].startMs < recentCutoff) continue;
    wakes.push({ minutes, atMs: sessions[i].startMs, group: timeOfDay(sessions[i - 1].endMs, tz) });
  }
  if (wakes.length < opts.minSamples)
    return insufficient(wakes.length, `Not enough data: ${wakes.length} of ${opts.minSamples} recent wake windows.`);

  // Basis: the most recently ended session (sessions are sorted by start).
  const last = sessions.reduce((a, b) => (b.endMs > a.endMs ? b : a));
  const est = estimate(wakes, timeOfDay(last.endMs, tz), nowMs, opts);
  const window = toWindow(last.endMs, est);
  let duration: Extract<SleepPrediction, { status: 'awake' }>['duration'] = null;
  if (durations.length >= opts.minSamples) {
    const d = estimate(durations, timeOfDay(window.pointMs, tz), nowMs, opts);
    duration = { medianMin: d.medianMin, lowerMin: d.lowerMin, upperMin: d.upperMin, sampleSize: d.sampleSize, group: d.group };
  }
  return { status: 'awake', modelVersion: SLEEP_MODEL_VERSION, experimental: true, basisEventId: last.id, basisMs: last.endMs, overdue: nowMs > window.endMs, window, medianMin: est.medianMin, sampleSize: est.sampleSize, group: est.group, widened: est.widened, duration };
}
